/**
 * Oynatma denetleyicisi — oynatıcı mantığının saf kısmı.
 *
 * Tasarım kararı: **kararı sunucu verir.** İstemci `decide()` çağırır,
 * dönen `mode` değerine göre iki yoldan birini kurar:
 *
 *   - `direct_play` / `direct_stream` → `<video src="/api/items/:id/stream">`
 *     (tarayıcı Range başlığını kendi yönetir; sunucu 206/416 döner)
 *   - `transcode` → iş hazır olunca playlist oynatıcıya bağlanır.
 *     Safari HLS'i yerel destekler; diğer tarayıcılar `hls.js` kullanır.
 *
 * `hls.js` **dinamik olarak** yüklenir: doğrudan oynatma yapılan oturumlar
 * (sunucudaki en sık yol) 100 kB'lık oynatıcı kütüphanesini indirmez.
 *
 * Dosya sistemi yolları istemciye hiç taşınmaz (spec §katalog).
 */
import type { PlaybackDecision, TranscodeJob } from './api.ts';

export type PlaybackSource =
  | { kind: 'direct'; url: string }
  | { kind: 'hls'; url: string; job: TranscodeJob };

export type PlaybackPhase =
  | { kind: 'idle' }
  | { kind: 'deciding' }
  | { kind: 'transcoding'; job: TranscodeJob; progressRatio: number | null }
  | { kind: 'ready'; source: PlaybackSource }
  | { kind: 'error'; message: string };

export type PlaybackEvent =
  | { kind: 'recovering'; message: string }
  /**
   * Kaynak **bağlandı**: manifest okundu, oynatma akışı başladı.
   *
   * Gerçek sunucuda bunun hiç gelmediği bir durum vardı: sunucu
   * `playable: true` diyordu, ekranda oynatıcı çubuğu vardı, ama oynatma
   * başlamıyordu ve konsolda tek bir hata yoktu. "Bağlandı" bilgisi olmadan
   * bu, başarısız bir bağlanmadan ayırt edilemiyordu — sessizlikti.
   */
  | { kind: 'manifest' }
  | { kind: 'error'; message: string };

export interface AttachResult {
  /** Oynatıcıya bağlanan yol (arayüzde gösterilebilir). */
  via: 'native' | 'hls.js' | null;
  error?: string;
}

/** HLS oynatıcısının ihtiyaç duyduğu en küçük arayüz (testlerde sahte verilir). */
export interface HlsLike {
  on(event: string, handler: (event: string, data: HlsErrorData) => void): void;
  loadSource(url: string): void;
  attachMedia(video: HTMLVideoElement): void;
  startLoad(): void;
  recoverMediaError(): void;
  destroy(): void;
}

export interface HlsErrorData {
  fatal: boolean;
  type: string;
  details?: string;
  reason?: string;
  error?: unknown;
  response?: unknown;
  frag?: unknown;
  [key: string]: unknown;
}

export interface HlsConstructor {
  new (config: Record<string, unknown>): HlsLike;
  /**
   * `MANIFEST_PARSED` bilinçle eklendi.
   *
   * Gerçek sunucuda kaynak atanıyor ama oynatma başlamıyordu ve konsolda tek
   * hata yoktu; "bağlandım" bilgisi yoktu. Bu olay, bağlanmanın gerçekten
   * tamamlandığını bildirir.
   */
  Events: {
    ERROR: string;
    MANIFEST_PARSED: string;
    FRAG_BUFFERED?: string;
    LEVEL_UPDATED?: string;
  };
  ErrorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string };
  isSupported(): boolean;
}

type VideoWithHls = HTMLVideoElement & { __hls?: HlsLike | undefined };

/** Doğrudan oynatma/aktarma kararında kaynak doğrudan kurulabilir. */
export function directSource(url: string): PlaybackSource {
  return { kind: 'direct', url };
}

/** Transcode kararı: kaynak ancak iş `ready` olduğunda oluşur. */
export function hlsSource(job: TranscodeJob): PlaybackSource {
  return { kind: 'hls', url: job.playlistUrl, job };
}

/** Karar + iş durumunu oynatıcı fazına çevirir. */
export function resolvePhase(
  decision: PlaybackDecision,
  directUrl: string,
  job: TranscodeJob | null,
  status: { state: string; progressRatio: number | null; playable?: boolean } | null,
): PlaybackPhase {
  if (decision.mode !== 'transcode') {
    return { kind: 'ready', source: directSource(directUrl) };
  }
  if (!job) return { kind: 'deciding' };
  if (status?.state === 'failed') {
    return { kind: 'error', message: 'Dönüştürme başarısız.' };
  }
  // `playable` sunucuda ilk segmentler hazır olduğunda yükselir; `state`
  // ancak FFmpeg bitince `ready` olur. Oyuncu beklemek zorunda kalmaz.
  if (status?.state === 'ready' || status?.playable === true) {
    return { kind: 'ready', source: hlsSource(job) };
  }
  return { kind: 'transcoding', job, progressRatio: status?.progressRatio ?? null };
}

/** Tarayıcının HLS'i yerel destekleyip desteklemediği. */
export function hasNativeHls(canPlayType: string | null): boolean {
  return canPlayType !== null && canPlayType !== '';
}

/** `hls.js` yükleyicisi. Varsayılan: dinamik `import()` (paketi bölmeye yarar). */
export const loadHlsJs: () => Promise<HlsConstructor> = async () => {
  const module = await import('hls.js');
  return (module.default ?? module) as unknown as HlsConstructor;
};

/**
 * `video` öğesini kaynağa bağlar.
 *
 * `hlsFactory` verilmezse `hls.js` **lazım olduğunda** yüklenir. Hata durumunda
 * çağıran `{ via: null, error }` alır; oynatıcı yarı bağlı bırakılmaz.
 */
export async function attachSource(
  video: HTMLVideoElement,
  source: PlaybackSource,
  onEvent: (event: PlaybackEvent) => void,
  hlsFactory?: HlsConstructor,
  startPositionSeconds = 0,
): Promise<AttachResult> {
  detachSource(video);

  if (source.kind === 'direct') {
    video.src = source.url;
    return { via: 'native' };
  }

  const Hls = hlsFactory ?? (await loadHlsJs());

  // MSE destekleyen tüm masaüstü ve modern tarayıcılarda (Edge, Chrome, Firefox)
  // `hls.js` önceliklidir. Windows Edge yerel Media Foundation HLS oynatıcısı
  // kademeli büyüyen event playlist'leri yenileyemez ve ilk segmentler bitince
  // (~12 sn) hata verir.
  if (Hls.isSupported()) {
    /*
     * Konumdan başlayan dönüştürme (kalite düşürmesi).
     *
     * Sunucu segmentleri `-output_ts_offset` ile **mutlak** kaynak saniyesinden
     * başlatır (ör. 600). hls.js ise playlist'teki parçaları 0'dan sayar.
     * `timelineOffset` bu farkı kapatır: parça başlangıçları 600'e kayar ve
     * oynatıcının `currentTime` değeri gerçek film saniyesi olur.
     *
     * ## Neden `actualStartMs`?
     *
     * FFmpeg giriş araması kaynağın en yakın **IDR** karesine atlar ve
     * kopyalama yapılamayacağı için oradan başlamak zorundadır. HEVC'de 600'deki
     * kare CRA olduğundan FFmpeg bir önceki IDR'ye geri atlar: istenen 600.0,
     * gerçekte başlanan **591.4** (yerel ölçüm). Sunucunun bildirdiği konumla
     * içeriğin konumu arasındaki bu kayma, istemcinin hesabını bozar ve
     * oynatma takılır.
     *
     * Bu yüzden sunucu **ölçülen** başlangıcı da bildirir (`actualStartMs`);
     * zaman çizelgesi içerikle birebir aynı değere kurulur.
     */
    const startMs = source.job.actualStartMs ?? source.job.startPositionMs ?? 0;
    const timelineOffset = source.kind === 'hls' ? Math.max(0, startMs / 1000) : 0;
    const playlistStart = Math.max(0, startPositionSeconds - timelineOffset);

    const hls = new Hls({
      // Segmentler sunucuda 4 sn; oynatıcı tamponunu buna göre ayarla.
      maxBufferLength: 30,
      // 30 sn yeter: geri sarma hissi için biraz daha uzun tutuyoruz.
      backBufferLength: 60,
      startPosition: playlistStart,
      timelineOffset,
    });

    const recoveredRef = { current: false };

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      onEvent({ kind: 'manifest' });
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      handleHlsError(hls, data, onEvent, recoveredRef, Hls.ErrorTypes);
    });

    hls.loadSource(source.url);
    hls.attachMedia(video);
    (video as VideoWithHls).__hls = hls;
    return { via: 'hls.js' };
  }

  // Yalnızca hls.js (MSE) desteklenmeyen tarayıcılarda (ör. iOS Safari) yerel HLS'e düş.
  if (hasNativeHls(video.canPlayType('application/vnd.apple.mpegurl'))) {
    video.src = source.url;
    return { via: 'native' };
  }

  return { via: null, error: 'Bu tarayıcı HLS oynatmayı desteklemiyor.' };
}

function handleHlsError(
  hls: HlsLike,
  data: HlsErrorData,
  onEvent: (event: PlaybackEvent) => void,
  recoveredRef: { current: boolean },
  errorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string },
): void {
  // `fatal` olmayan hatalar oynatıcı kendi kendine toparlanır.
  if (!data.fatal) return;

  if (data.type === errorTypes.NETWORK_ERROR) {
    hls.startLoad();
    onEvent({ kind: 'recovering', message: 'Ağ hatası, yeniden deneniyor' });
    return;
  }

  if (data.type === errorTypes.MEDIA_ERROR) {
    if (!recoveredRef.current) {
      recoveredRef.current = true;
      hls.recoverMediaError();
      onEvent({ kind: 'recovering', message: 'Medya hatası, onarılıyor' });
    } else {
      onEvent({ kind: 'error', message: 'Medya hatası: akış onarılamadı.' });
      hls.destroy();
    }
    return;
  }

  onEvent({ kind: 'error', message: 'Oynatma durduruldu: akış okunamadı.' });
  hls.destroy();
}

/**
 * "Kaldığın yerden devam et" atlama işleyicisi.
 *
 * ## Neden geri çağırma?
 *
 * Gerçek sunucuda oynatma **0:11'de çivilenmişti**: oynatıcı ilerlemiyor,
 * hls.js ise 9:36'ya (144. segment) kadar segment çekiyordu. Ağ sağlam,
 * tampon doluydu, konsolda hata yoktu.
 *
 * Sebep: `loadedmetadata` olayına bağlanan bir geri çağırma **koşulsuz**
 * `video.currentTime = resumeAt` yapıyordu. Ama kademeli (büyüyen) playlist'te
 * bu olay **tekrarlanır** — playlist büyüdükçe süre değişir ve hls.js medyayı
 * yeniden bildirir. Her tekrar oynatıcıyı aynı konuma geri atıyordu.
 *
 * Düzeltme: **yalnızca bir kez** atla.
 */
export function createResumeSeek(
  video: HTMLVideoElement,
  resumeAtSeconds: number,
  onPlayBlocked: () => void,
): () => void {
  let seeked = false;

  return (): void => {
    if (!seeked) {
      seeked = true;
      if (resumeAtSeconds > 0) video.currentTime = resumeAtSeconds;
    }
    void video.play().catch(onPlayBlocked);
  };
}

/** Aktif kaynağı ayırır.
 *
 * Atlatmada segmentler hazır olduğu kadar kalıcı olmalıdır; bu yüzden burada
 * önbellek temizlenmez, yalnız akış durdurulur.
 */
export function detachSource(video: HTMLVideoElement): void {
  const holder = video as VideoWithHls;
  if (holder.__hls) {
    holder.__hls.destroy();
    holder.__hls = undefined;
  }
  video.removeAttribute('src');
  video.load();
}
